<template>
  <main class="container-1 terrain-crew-form">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <h2>{{ isEditing ? 'Editar cuadrilla' : 'Nueva cuadrilla' }}</h2>
    </div>

    <section class="container-body terrain-crew-form__body" aria-live="polite">
      <p v-if="loading" class="terrain-crew-form__status">Cargando cuadrilla…</p>

      <template v-else>
        <div v-if="isEditing" class="terrain-crew-form__status-bar">
          <TerrainBadge :type="active ? 'success' : 'secondary'" :message="active ? 'Activa' : 'Inactiva'" />
        </div>

        <p v-if="errorMessage" class="terrain-crew-form__error" role="alert">{{ errorMessage }}</p>

        <label class="terrain-crew-form__field">
          Nombre de la cuadrilla
          <input v-model="name" type="text" :disabled="saving">
        </label>

        <label class="terrain-crew-form__field">
          Descripción
          <textarea v-model="description" :disabled="saving" rows="3" />
        </label>

        <fieldset class="terrain-crew-form__fieldset">
          <legend>Miembros</legend>

          <div class="terrain-crew-form__group-add">
            <SearchSelect
              v-model="selectedGroupId"
              :options="groupOptions"
              :loading="groupsLoading"
              :disabled="saving || addingGroupMembers"
              placeholder="Agregar por grupo…"
              empty-message="Sin grupos"
              @search="searchGroups"
            />
            <button
              type="button"
              class="terrain-crew-form__group-add-btn"
              :disabled="!selectedGroupId || saving || addingGroupMembers"
              @click="addGroupMembers"
            >
              {{ addingGroupMembers ? 'Agregando…' : 'Agregar integrantes' }}
            </button>
          </div>

          <TagMultiSelect
            :value="memberEmails"
            :options="userOptions"
            :loading="usersLoading"
            :disabled="saving"
            :allow-custom="true"
            :custom-validator="isValidEmail"
            :custom-normalizer="normalizedEmail"
            placeholder="Buscar, agregar o pegar correos separados por ;…"
            empty-message="Sin usuarios"
            @input="updateMemberEmails"
            @search="searchUsers"
          />
        </fieldset>

        <div class="terrain-crew-form__actions">
          <button type="button" class="terrain-crew-form__primary" :disabled="saving" @click="onSave">
            {{ saving ? 'Guardando…' : 'Guardar' }}
          </button>
        </div>
      </template>
    </section>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import TerrainBadge from "@/components/terrain/TerrainBadge.vue";
import TagMultiSelect from "@/components/common/pickers/TagMultiSelect.vue";
import SearchSelect from "@/components/common/pickers/SearchSelect.vue";
import { createTerrainApi } from "@/services/terrain/api.js";

export default {
  name: "TerrainCrewForm",
  components: { Breadcrumb, TerrainBadge, TagMultiSelect, SearchSelect },
  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: "",
      resourceVersion: 0,
      active: true,
      name: "",
      description: "",
	  memberEmails: [],
      userOptions: [],
      usersLoading: false,
      groupOptions: [],
      groupsLoading: false,
      selectedGroupId: null,
      addingGroupMembers: false,
    };
  },
  computed: {
    crewId() { return this.$route.params.crewId || null; },
    isEditing() { return Boolean(this.crewId); },
    routesBread() {
      return [
        { name: "Terreno" },
        { name: "Cuadrillas", path: "/terreno/cuadrillas" },
        { name: this.isEditing ? "Editar" : "Nueva" },
      ];
    },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
  },
  created() { this.initialize(); },
  watch: {
    "$route.params.crewId"() { this.initialize(); },
  },
  methods: {
    async initialize() {
      this.errorMessage = "";
      this.searchUsers("");
      this.searchGroups("");
      if (this.crewId) {
        await this.loadCrew();
      } else {
        this.name = "";
        this.description = "";
		this.memberEmails = [];
        this.active = true;
        this.resourceVersion = 0;
      }
    },
    async loadCrew() {
      this.loading = true;
      try {
        const response = await this.terrainApi.getCrew(this.crewId);
        const crew = response.data;
        this.name = crew.name;
        this.description = crew.description || "";
		this.memberEmails = (crew.memberEmails || []).map((email) => this.normalizedEmail(email));
        this.active = crew.active;
        this.resourceVersion = crew.resourceVersion;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar la cuadrilla.";
      } finally {
        this.loading = false;
      }
    },
    searchUsers(query) {
      this.usersLoading = true;
      const backendurl = this.$store.getters.backendurl;
      this.$http.get(`${backendurl}api/v1/users`, { params: { page: 1, size: 25, search: query || "" } })
        .then((response) => {
          const fetched = (response.data || []).map((user) => ({
			id: String(user.username || "").trim().toLowerCase(),
            label: `${user.firstname || ""} ${user.lastname || ""}`.trim() || user.username,
            sublabel: user.username,
          }));
          // Se fusiona con lo ya cargado (no se reemplaza): de lo contrario
          // una búsqueda nueva le haría perder el chip a miembros ya
          // agregados (por búsqueda previa o por grupo) que no aparezcan en
          // estos resultados, aunque sigan seleccionados en memberUserIds.
          const merged = new Map(this.userOptions.map((option) => [option.id, option]));
          fetched.forEach((option) => merged.set(option.id, option));
          this.userOptions = Array.from(merged.values());
        })
        .catch(() => {})
        .finally(() => { this.usersLoading = false; });
    },
    searchGroups(query) {
      this.groupsLoading = true;
      const backendurl = this.$store.getters.backendurl;
      this.$http.get(`${backendurl}api/v1/groups/`)
        .then((response) => {
          const all = (response.data || []).map((group) => ({ id: group.id, label: group.name }));
          const normalized = (query || "").trim().toLowerCase();
          this.groupOptions = normalized ? all.filter((group) => group.label.toLowerCase().includes(normalized)) : all;
        })
        .catch(() => { this.groupOptions = []; })
        .finally(() => { this.groupsLoading = false; });
    },
    async addGroupMembers() {
      if (!this.selectedGroupId) return;
      this.addingGroupMembers = true;
      try {
        const backendurl = this.$store.getters.backendurl;
        const response = await this.$http.get(`${backendurl}api/v1/groups/${this.selectedGroupId}/users/`);
        const users = response.data || [];
        const fetched = users.map((user) => ({
          id: user.id,
          label: `${user.firstname || ""} ${user.lastname || ""}`.trim() || user.username,
          sublabel: user.username,
        }));
        const merged = new Map(this.userOptions.map((option) => [option.id, option]));
        fetched.forEach((option) => merged.set(option.id, option));
        this.userOptions = Array.from(merged.values());

		const currentIds = new Set(this.memberEmails);
		const addedIds = fetched.map((option) => this.normalizedEmail(option.sublabel)).filter((email) => email && !currentIds.has(email));
		this.memberEmails = [...this.memberEmails, ...addedIds];
        this.selectedGroupId = null;
        this.$store.dispatch("toast/show", {
          message: addedIds.length
            ? `Se agregaron ${addedIds.length} integrante(s) del grupo.`
            : "Todos los integrantes del grupo ya estaban agregados.",
          type: "success",
        });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo cargar los integrantes del grupo.", type: "error" });
      } finally {
        this.addingGroupMembers = false;
      }
    },
    normalizedEmail(value) { return String(value || "").trim().toLowerCase(); },
    isValidEmail(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); },
    updateMemberEmails(emails) {
      this.memberEmails = [...new Set((emails || []).map(this.normalizedEmail).filter(Boolean))];
    },
    async onSave() {
      this.errorMessage = "";
      if (!this.name.trim()) {
        this.errorMessage = "El nombre de la cuadrilla es obligatorio.";
        return;
      }
      this.saving = true;
      try {
		const payload = { name: this.name.trim(), description: this.description.trim(), memberEmails: this.memberEmails };
        const response = this.isEditing
          ? await this.terrainApi.updateCrew(this.crewId, { ...payload, expectedVersion: this.resourceVersion })
          : await this.terrainApi.createCrew(payload);
        const body = response.data;
        this.resourceVersion = body.resourceVersion;
        this.$store.dispatch("toast/show", { message: "Cuadrilla guardada correctamente.", type: "success" });
        if (!this.isEditing) {
          this.$router.replace({ name: "terrain-crew-edit", params: { crewId: body.id } });
        }
      } catch (error) {
        const data = error.response && error.response.data;
        const code = data && data.code;
        this.errorMessage = code === "TERRAIN_CREW_NAME_CONFLICT"
          ? "Ya existe una cuadrilla activa con ese nombre."
          : code === "TERRAIN_STALE_RESOURCE"
            ? "La cuadrilla fue modificada por otra solicitud; recargue e intente de nuevo."
            : (data && data.message) || "No se pudo guardar la cuadrilla.";
        this.$store.dispatch("toast/show", { message: this.errorMessage, type: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-crew-form__body {
  display: grid;
  gap: var(--terrain-space-xl);
  margin: 0 auto;
  max-width: 640px;
  width: 95%;
}

.terrain-crew-form__status-bar { display: flex; }

.terrain-crew-form__field {
  display: grid;
  gap: var(--terrain-space-xs);
  color: var(--color-navy);
  font-weight: 600;
  font-size: var(--terrain-font-caption);
}

.terrain-crew-form__field input,
.terrain-crew-form__field textarea {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
  &:disabled { background: var(--color-mist); cursor: not-allowed; }
}

.terrain-crew-form__field textarea { resize: vertical; }

.terrain-crew-form__fieldset {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-lg);
  display: grid;
  gap: var(--terrain-space-md);

  legend {
    padding: 0 var(--terrain-space-xs);
    color: var(--color-navy);
    font-weight: 700;
  }
}

.terrain-crew-form__group-add {
  align-items: flex-start;
  display: flex;
  gap: var(--terrain-space-sm);

  > .search-select { flex: 1; }
}

.terrain-crew-form__group-add-btn {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-md);
  color: var(--color-navy);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  white-space: nowrap;
  transition: background-color var(--terrain-transition), color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-navy); color: var(--color-white); }
  &:disabled { background: var(--color-white); border-color: var(--color-line); color: var(--color-ghost); cursor: not-allowed; }
}

.terrain-crew-form__actions { display: flex; gap: var(--terrain-space-md); }

.terrain-crew-form__primary {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}

.terrain-crew-form__error {
  margin: 0;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: var(--terrain-color-danger-light);
  border-left: 4px solid var(--color-danger);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-danger);
}

.terrain-crew-form__status { color: var(--color-ghost); margin: 0; }
</style>
